import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  AllData,
  Filtro,
  Variable_indicator,
} from "../../../../../interfaces/Indicator";
import { INDICATOR_TYPE_PROCESS, INDICATOR_TYPE_RESULTS } from "../../../../../utils/const";
import { filterByCriteria } from "../../../../../utils/utils";
import { useLocation } from "react-router-dom";
import Button from "../../../../../components/Buttons/Button";
import { MdOutlineFilterAlt } from "react-icons/md";
import { ISelectedItem } from "../../../../../components/Inputs/Selects/MultiSelectCheck";
import FilterInputs from "./FilterInputs";
import SideBar from "../../../../../components/SideBar/SideBar";
import FiltersInputsSideBar from "./FiltersInputsSideBar";
import { filterByCriteriaDualAxis } from "../../../utils/filterByCriteriaDualAxis";
import { areFiltersEqual, buildInitialFilters, buildInitialMapFilters, filterAndGroupMapDataByComuna, filterMapData, isHeatMapGraph, isMapGraph } from "./helpers";

export type GraphType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | number;
export type FilterMap = Record<string, string[]>;

export interface TransformedVariable {
  name: string;
  values: Array<{ value: string; id: number }>;
  orden?: number;
  axisYLeft?: boolean;
  axisYRight?: boolean;
}

export interface FilterOfGraphsProps {
  typeOfIndicator: string;
  data: AllData[];
  setDataChart: (data: any[]) => void;
  variable_analisis: string[];
  variable_analisis_axis_y_right: Variable_indicator | null;
  cleanFilters: boolean;
  setCleanFilters: React.Dispatch<React.SetStateAction<boolean>>;
  setFiltersSelected: React.Dispatch<React.SetStateAction<FilterMap>>;
  isPercentageChart: null | 1 | 0;
  handleCleanFilters: () => void;
  filters: Filtro[];
  typeOfGraph: GraphType;
  setErrorNoData: React.Dispatch<React.SetStateAction<boolean>>;
}

export const MAP_GRAPH_TYPES = [6, 7] as const;

const FilterOfGraphs = ({
  typeOfIndicator,
  data,
  setDataChart,
  variable_analisis,
  variable_analisis_axis_y_right,
  cleanFilters,
  setCleanFilters,
  setFiltersSelected,
  handleCleanFilters,
  filters,
  typeOfGraph,
  setErrorNoData,
}: FilterOfGraphsProps) => {

  const location = useLocation();

  const [variables, setVariables] = useState<Filtro[]>([]);
  const [selectedVariables, setSelectedVariables] = useState<FilterMap>({});
  const [isSideBarOpen, setIsSideBarOpen] = useState(false);
  const [haveChanges, setHaveChanges] = useState(false);
  const initialFiltersRef = useRef<FilterMap>({});

  /**
   * Determina si se debe omitir la inicialización de filtros
   */
  const shouldSkipInitialization = useMemo(() => {
    return !variable_analisis?.length || typeOfIndicator === INDICATOR_TYPE_PROCESS
  }, [variable_analisis, typeOfIndicator]);

  const applyDataFilter = useCallback((filterVariables: FilterMap) => {
    const analysisVariable = variable_analisis?.[0] || '';

    return !variable_analisis_axis_y_right
      ? filterByCriteria(filterVariables, data, filters, analysisVariable, null)
      : filterByCriteriaDualAxis(
        filterVariables,
        data,
        analysisVariable,
        variable_analisis_axis_y_right,
        filters
      );
  }, [variable_analisis_axis_y_right, data, variable_analisis, filters]);

  /**
   * Filtra los datos de mapa: mapas base (tipo 6) filtran por coincidencia
   * exacta; mapas de calor (tipo 7) además agrupan y suman por comuna.
   */
  const applyMapFilter = useCallback((filterVariables: FilterMap) => {
    return isHeatMapGraph(typeOfGraph)
      ? filterAndGroupMapDataByComuna(filterVariables, data)
      : filterMapData(data, filterVariables);
  }, [typeOfGraph, data]);

  /**
   * Aplica filtros a los datos y actualiza el gráfico
   */
  const applyFiltersAndUpdate = useCallback((updatedVariables: FilterMap) => {
    try {
      const filteredData = applyDataFilter(updatedVariables)
      setDataChart(filteredData);
      setFiltersSelected(updatedVariables);
    } catch (error) {
      console.error('Error applying filters:', error);
      setErrorNoData(true);
    }
  }, [
    variable_analisis_axis_y_right,
    data,
    variable_analisis,
    filters,
    setDataChart,
    setFiltersSelected,
    setErrorNoData
  ]);

  /**
   * Verifica si los filtros han sido modificados respecto al estado inicial
   */
  const areFiltersModified = useCallback((): boolean => {
    return !areFiltersEqual(initialFiltersRef.current, selectedVariables);
  }, [selectedVariables]);

  /**
   * Maneja la selección de una variable simple (select único)
   */
  const handleSelectVariable = useCallback((label: string, value?: string) => {
    setSelectedVariables((prevSelected) => {
      const updated = {
        ...prevSelected,
        [label]: [value || ''],
      };

      // Detectar cambios para activar el indicador
      const current = prevSelected[label]?.[0] || '';
      if (current !== value) {
        setHaveChanges(true);
      }

      // Lógica específica para gráficos de mapa: combina todos los filtros seleccionados (año y demás)
      if (isMapGraph(typeOfGraph)) {
        const filteredDataMap = applyMapFilter(updated);
        setDataChart(filteredDataMap);
        setErrorNoData(filteredDataMap.length === 0);
        setFiltersSelected(updated);
      }

      return updated;
    });
  }, [typeOfGraph, applyMapFilter, setDataChart, setErrorNoData, setFiltersSelected]);

  /**
   * Maneja la selección múltiple de variables (multi-select)
   */
  const handleSelectMultipleVariables = useCallback(({ value, label }: ISelectedItem) => {
    setSelectedVariables((prevSelected) => {
      const prevValues = prevSelected[label] || [];
      const allOptions = filters.find(v => v.name === label)?.values.filter((v) => v.value !== "Todos").map((values) => values.value) || [];

      const hasTodosOption = variables?.find((v) => v.name === label)?.values.find((v) => v.value === "Todos");

      let newValues = new Set(prevValues);

      // Manejo de la opción "Todos"
      if (value === "Todos") {
        newValues = new Set(["Todos", ...allOptions]);
      } else {
        if (prevValues.includes("Todos")) {
          newValues = new Set([value]);
        } else {
          if (newValues.has(value)) {
            newValues.delete(value);
          } else {
            newValues.add(value);
          }

          // Auto-seleccionar "Todos" si todos los valores están seleccionados
          const allValues = allOptions.map(opt => opt);
          const isAllSelected = allValues.every(v => newValues.has(v));

          if (isAllSelected) {
            hasTodosOption !== undefined
              ? newValues.add("Todos")
              : newValues = new Set(allValues);
          }
        }
      }

      const updatedVariables = {
        ...prevSelected,
        [label]: Array.from(newValues)
      };

      // Detectar cambios
      const isSame = JSON.stringify(updatedVariables[label]) ===
        JSON.stringify(prevSelected[label]);
      if (!isSame) {
        setHaveChanges(true);
      }

      // Aplicar filtros solo para gráficos no-mapa
      if (!isMapGraph(typeOfGraph)) {
        applyFiltersAndUpdate(updatedVariables);
      }

      return updatedVariables;
    });
  }, [
    variables,
    typeOfGraph,
    applyFiltersAndUpdate
  ]);

  useEffect(() => {
    setVariables(filters);
  }, [typeOfGraph, filters]);

  /**
   * Maneja la limpieza de filtros cuando se activa cleanFilters
   */
  useEffect(() => {
    if (!cleanFilters) return;

    const resetFilters = initialFiltersRef.current;

    setSelectedVariables(resetFilters);
    setFiltersSelected(resetFilters);

    if (isMapGraph(typeOfGraph)) {
      const filteredDataMap = applyMapFilter(resetFilters);
      setDataChart(filteredDataMap);
      setErrorNoData(filteredDataMap.length === 0);
      setCleanFilters(false);
      return;
    }

    const filteredData = applyDataFilter(resetFilters)
    setDataChart(filteredData);
    setCleanFilters(false);
  }, [
    cleanFilters,
    typeOfGraph,
    applyMapFilter,
    variable_analisis_axis_y_right,
    data,
    variable_analisis,
    filters,
    setDataChart,
    setFiltersSelected,
    setErrorNoData,
    setCleanFilters
  ]);

  /**
   * Inicializa los filtros al cargar las variables
   */
  useEffect(() => {
    if (isMapGraph(typeOfGraph)) {
      const params = new URLSearchParams(location.search);
      const period = params.get("period") || "";
      const initialSelected = buildInitialMapFilters(variables, period);

      initialFiltersRef.current = initialSelected;
      setSelectedVariables(initialSelected);
      setFiltersSelected(initialSelected);

      const filteredDataMap = applyMapFilter(initialSelected);
      setDataChart(filteredDataMap);
      setErrorNoData(filteredDataMap.length === 0);
      return;
    }

    if (shouldSkipInitialization) return;

    const initialSelected = buildInitialFilters(variables, variable_analisis);
    initialFiltersRef.current = initialSelected;
    setSelectedVariables(initialSelected);
    setFiltersSelected(initialSelected);
    const filteredData = applyDataFilter(initialSelected)

    setDataChart(filteredData);
  }, [shouldSkipInitialization, variables, typeOfGraph, location, applyMapFilter]);

  /**
   * Maneja la detección de datos vacíos para gráficos no-mapa
   * (para mapas, el error de datos vacíos ya lo resuelve el efecto de inicialización)
   */
  useEffect(() => {
    // Los indicadores de proceso no muestran error de datos vacíos
    if (typeOfIndicator === INDICATOR_TYPE_PROCESS) {
      setErrorNoData(false);
      return;
    }

    if (isMapGraph(typeOfGraph)) return;

    setErrorNoData(!data || data.length === 0);
  }, [typeOfIndicator, typeOfGraph, data, setErrorNoData]);


  // No renderizar si no es un indicador de resultados o si no tienen filtros con valores
  // EXCEPCIÓN: los mapas siempre se renderizan (si son indicadores de resultados)
  if (typeOfIndicator !== INDICATOR_TYPE_RESULTS) {
    return null;
  }

  if (!isMapGraph(typeOfGraph) && !filters.length) {
    return null;
  }

  const commonProps = {
    variables,
    typeOfGraph,
    handleSelectVariable,
    selectedVariables,
    handleSelectMultipleVariables,
    areFiltersModified,
    handleCleanFilters,
  };

  return (
    <>
      {/* Vista móvil con sidebar */}
      <div className="md:hidden">
        <Button
          label="Filtrar"
          icon={MdOutlineFilterAlt}
          showIconRight={true}
          showIconLeft={false}
          onClick={() => setIsSideBarOpen(true)}
          className="border border-system-light-50 rounded-lg text-system-light-40 text-llarge h-8 !px-4 !h-[32px]"
          iconSize={18}
        />

        <SideBar isOpen={isSideBarOpen} onClose={() => setIsSideBarOpen(false)}>
          {isSideBarOpen && (
            <FiltersInputsSideBar
              {...commonProps}
              onCloseSideBar={() => setIsSideBarOpen(false)}
              setHaveChanges={setHaveChanges}
              haveChanges={haveChanges}
            />
          )}
        </SideBar>
      </div>

      {/* Vista desktop */}
      <div className="hidden md:flex wrap gap-3 items-end">
        <FilterInputs {...commonProps} />
      </div>
    </>
  );
};

export default FilterOfGraphs